import React, { useCallback, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FaEye, FaEyeSlash } from "react-icons/fa6";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { axiosInstanceWithToken } from "@/helpers/axios";

// Password Validation Schema
const PASSWORD_VALIDATION_SCHEMA = Yup.object().shape({
    current_password: Yup.string().required("Current password is required."),
    password: Yup.string()
        .required("New password is required")
        .min(8, "New password must be at least 8 characters")
        .matches(/[a-z]/, "New password must contain at least one lowercase letter")
        .matches(/[A-Z]/, "New password must contain at least one uppercase letter")
        .matches(/[0-9]/, "New password must contain at least one number")
        .matches(
            /[@$!%*?&#.,;:'"`~^+=_(){}[\]|\\/<>\-]/,
            "New password must contain at least one special character"
        )
        .test(
            "not-common-password",
            "This password is too common. Please choose a more secure password.",
            (value) => !isCommonPassword(value) // Add your common password check logic here
        ),
    password_confirmation: Yup.string()
        .required("Confirm password is required")
        .oneOf([Yup.ref("password"), null], "Passwords must match"),
});

// Mock function for common password check
const isCommonPassword = (password) => {
    const commonPasswords = ["password", "12345678", "qwerty", "admin"];
    return commonPasswords.includes(password);
};

export const PasswordSection = ({ data }) => {
    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
    } = useForm({
        resolver: yupResolver(PASSWORD_VALIDATION_SCHEMA),
    });

    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const currentPassword = watch("current_password");
    const newPassword = watch("password");

    // Toggle visibility functions
    const toggleCurrentVisibility = () => setShowCurrentPassword(!showCurrentPassword);
    const togglePasswordVisibility = () => setShowPassword(!showPassword);
    const togglePasswordConfirm = () => setShowConfirmPassword(!showConfirmPassword);

    // Handle form submission
    const onSubmit = useCallback(async (formData) => {
        try {
            // Make the API request
            await axiosInstanceWithToken.post("/auth/change-password", formData);

            // Show success message
            Swal.fire({
                icon: "success",
                title: "Success!",
                text: "Your password has been updated successfully.",
            });
        } catch (err) {
            console.error(err);

            // Extract error message from the error response
            const errorMessage = err.response?.data?.message || "An error occurred while updating your password.";

            // Show error message
            Swal.fire({
                icon: "error",
                title: "Error!",
                text: errorMessage,
            });
        }
    }, []);

    // Log validation errors
    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            console.log("Validation Errors:", errors);
        }
    }, [errors]);

    return (
        <div className="profile-edit-container fl-wrap block_box">
            <div className="custom-form">
                <div className="row">
                    {/* Current Password Field */}
                    <div className="relative col-sm-12">
                        <label>
                            Current Password <i className="fal fa-lock" />
                        </label>
                        <input
                            type={showCurrentPassword ? "text" : "password"}
                            placeholder="Current Password"
                            {...register("current_password")}
                        />
                        <span className="absolute md:right-8 top-12 right-8" onClick={toggleCurrentVisibility}>
                            {showCurrentPassword ? <FaEye /> : <FaEyeSlash />}
                        </span>
                        {errors.current_password && (
                            <p className="error-message" style={{ color: "red" }}>
                                {errors.current_password.message}
                            </p>
                        )}
                        {currentPassword !== data?.password && currentPassword && (
                            <p className="error-message" style={{ color: "red" }}>
                                Please make sure your current password is correct.
                            </p>
                        )}
                    </div>

                    {/* New Password Field */}
                    <div className="relative col-sm-6">
                        <label>
                            New Password <i className="fal fa-lock" />
                        </label>
                        <input
                            type={showPassword ? "text" : "password"}
                            placeholder="New Password"
                            {...register("password")}
                        />
                        <span className="absolute md:top-[3.3rem] md:right-8 top-[8.3rem] right-8" onClick={togglePasswordVisibility}>
                            {showPassword ? <FaEye /> : <FaEyeSlash />}
                        </span>
                        {errors.password && (
                            <p className="error-message" style={{ color: "red" }}>
                                {errors.password.message}
                            </p>
                        )}
                    </div>

                    {/* Confirm Password Field */}
                    <div className="relative col-sm-6">
                        <label>
                            Confirm Password <i className="fal fa-lock" />
                        </label>
                        <input
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder="Confirm Password"
                            {...register("password_confirmation")}
                        />
                        <span className="absolute md:top-[3.3rem] md:right-8 top-[13.3rem] right-8" onClick={togglePasswordConfirm}>
                            {showConfirmPassword ? <FaEye /> : <FaEyeSlash />}
                        </span>
                        {errors.password_confirmation && (
                            <p className="error-message" style={{ color: "red" }}>
                                {errors.password_confirmation.message}
                            </p>
                        )}
                    </div>
                </div>

                {/* Submit Button */}
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <button type="button" className="btn" onClick={handleSubmit(onSubmit)}>
                        <span style={{ color: "white", fontWeight: "700" }}>Update Password</span>
                    </button>
                </div>
            </div>
        </div>
    );
};